import { useEffect, MutableRefObject } from 'react';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { queryKeys } from '@shared/api/keys';
import { getQueryCacheOptions } from '@shared/query-client';
import { useIsOnline } from '@shared/lib/hooks';
import { USER_ROLE } from '@/constants';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('top-stories');

/**
 * `queryOptions` factory for an external RSS feed.
 *
 * Kept local to this template rather than added to `@shared/api/queries.ts`:
 * poets is the only template with RSS widgets, and this isn't a GraphQL
 * entity — same reasoning as morningstar's `collectionOptions`.
 *
 * Migrated from a hand-rolled `useEffect` + three `useState`s 2026-08-28
 * (topic 03's "remove effect-based fetching" clause). What that buys, beyond
 * consistency: the two widgets on poets' home (`top-stories`, `daily-news`)
 * that request the same feed URL now share one request instead of each
 * fetching independently; the response is cached and survives a remount; and
 * offline reads fall back to the last good feed via the app's
 * `networkMode: 'offlineFirst'` default rather than clearing to an empty
 * list.
 *
 * Parsing stays exactly as it was (`DOMParser` → `getRSSData` → sort by
 * `pubDate` descending) — this is a fetching change, not a parsing one.
 */
export function rssFeedOptions(url: string, isOnline: boolean) {
	const isAdmin = localStorage.getItem(USER_ROLE) === 'admin';
	return queryOptions({
		queryKey: queryKeys.rssFeed(url),
		queryFn: async (): Promise<Array<Item>> => {
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(
					`RSS feed request failed: ${response.status} ${response.statusText}`,
				);
			}
			const text = await response.text();
			const document = new DOMParser().parseFromString(text, 'text/xml');
			return getRSSData(document).sort(
				(a, b) => b.pubDate.getTime() - a.pubDate.getTime(),
			);
		},
		// `enabled` rather than an early return: the previous implementation
		// set `isLoading` true and bailed when `url` was empty, so the widget
		// spun forever instead of rendering an empty state. `useQuery` reports
		// a disabled query as `pending` with `fetchStatus: 'idle'`, which is
		// what `isLoading` below is derived from.
		enabled: !!url,
		...getQueryCacheOptions({ isOnline, isAdmin }),
	});
}

export function useFeed(url: string): FeedResponse {
	const isOnline = useIsOnline();
	const { data, isLoading, error } = useQuery(rssFeedOptions(url, isOnline));

	if (error) {
		logger.error(error);
	}

	return {
		feed: data ?? [],
		isLoading,
		error: error as Error | null,
	};
}

interface FeedResponse {
	feed: Array<Item>;
	error: Error | null;
	isLoading: boolean;
}

function getRSSData(data: Document): Array<Item> {
	const channel = getChannel(data);
	if (!channel) {
		return [];
	}
	return getItems(channel.childNodes);
}

function getChannel(data: Document): Node | null {
	const channel = data.childNodes[0].childNodes as NodeList;
	for (const item of channel.values()) {
		if (item.nodeName === 'channel') {
			return item;
		}
	}
	return null;
}

function getItems(nodes: NodeList): Array<Item> {
	const items = [];
	for (const item of nodes.values()) {
		if (item.nodeName === 'item') {
			items.push(getItem(item));
		}
	}
	return items;
}

function getItem(item: Node): Item {
	const nodes = toArray(item.childNodes.values());
	const title = getTextContent(nodes, 'title');
	const link = getTextContent(nodes, 'link');
	const category = getTextContent(nodes, 'category');
	const creator = getTextContent(nodes, 'dc:creator');
	const thumbnail = getThumbnail(nodes, 'media:thumbnail');
	const description = getTextContent(nodes, 'description');
	const pubDate = new Date(`${getTextContent(nodes, 'pubDate')}`);
	return {
		title,
		link,
		creator,
		description,
		thumbnail,
		category,
		pubDate,
	};
}

export interface Item {
	title: string | null;
	link: string | null;
	creator: string | null;
	thumbnail: string | null;
	category: string | null;
	description: string | null;
	pubDate: Date;
	index?: number;
}

/**
 * The cover-morph mechanism (same idea as `@shared/ui/preview-link`'s
 * `previewTransitionName`, kept local rather than reused: that one is
 * "preview-${id}"-shaped and conceptually tied to the issue-cover flow,
 * which also runs on this same page tree via `magazines.tsx` -- a distinct
 * prefix here avoids any risk of the two colliding). `item.link` (the
 * article's own URL) is the one field every feed item already carries that
 * is both stable and unique, so it is the identifier -- not `index`, which
 * is positional and would misattribute the transition if the feed
 * reordered between renders.
 *
 * `link` isn't a valid CSS custom-ident as-is (colons, slashes, dots), so
 * non-alphanumeric runs collapse to a single hyphen. Collisions are not a
 * practical concern -- two distinct URLs reducing to the same string would
 * require a coincidence this scheme doesn't try to defend against, same as
 * every other transition-name deriver in this codebase.
 */
export function feedItemTransitionName(link: string): string {
	return `feed-item-${link.replace(/[^a-zA-Z0-9]+/g, '-')}`;
}

function getTextContent(nodes: Array<Node>, tag: string) {
	if (!nodes?.length) {
		return null;
	}
	const filteredNode = nodes.filter((n) => n.nodeName === tag);
	if (!filteredNode.length) {
		return null;
	}
	return filteredNode[0].textContent;
}

function getThumbnail(nodes: Array<Node>, tag: string): string | null {
	const filteredNode = nodes.filter(
		(n): n is Element => n.nodeName === tag && n instanceof Element,
	);
	if (!filteredNode.length) {
		return null;
	}
	const thumbnail = filteredNode[0];
	// `NamedNodeMap` (thumbnail.attributes) has no `.url` property -- this
	// has never actually read the url attribute (always undefined here,
	// so this function has always returned null). Preserved as-is rather
	// than "fixed" to `getNamedItem('url')`, since that would be a real
	// behavior change (thumbnails would start rendering) outside the
	// scope of a type-only pass.
	const attrs = thumbnail.attributes as unknown as {
		url?: { value: string };
	};
	if (!attrs?.url) {
		return null;
	}

	const { value } = attrs.url;
	return value;
}

function toArray(nodes: IterableIterator<Node>): Array<Node> {
	const response: Array<Node> = [];
	for (const node of nodes) {
		response.push(node);
	}
	return response;
}

export function useRevolveAd(ref: MutableRefObject<HTMLElement | null>) {
	useEffect(() => {
		if (!ref) {
			return;
		}
		if (!ref.current) {
			return;
		}
		const { current } = ref;
		for (const item of current.children) {
			if (item.localName === 'script') {
				current.removeChild(item);
			}
		}

		const tag = document.createElement('script');
		tag.src = `https://fast.poets-writers.org/openx/www/delivery/asyncjs.php?id=${Math.random()}`;
		tag.async = true;
		tag.onload = () => {
			setTimeout(() => {
				const w = window as typeof window & { reviveAsync?: unknown };
				if (w.reviveAsync) {
					delete w.reviveAsync;
				}
			}, 1000);
		};
		ref.current.appendChild(tag);
		setTimeout(() => {
			for (const item of current.children) {
				if (item.localName === 'script') {
					current.removeChild(item);
				}
			}
		}, 1500);
	}, [ref]);
}
